import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import { reportError } from 'src/easyclaim.app.libs/reportError';
import styled from 'styled-components';

import { EasyClaimDocumentType } from '../easyclaim.api.client.typescript/src/index';
import {
	PlaceholderFrontV2,
	PlaceholderRearV2,
	PlaceholderLeftSideV2,
	PlaceholderRightSideV2,
} from '../easyclaim.app.assets/index';
import {
	DetailsContainer,
	SelectImage,
	ImageFileInputType,
	StyledInfoIcon,
	ExampleModalImage,
} from '../easyclaim.app.components/index';
import { ImageSelectionService } from '../easyclaim.app.services';

import { ScreenComponent } from './ScreenComponent';

interface PolicyCheckVehicleImagesState {}

export class PolicyCheckVehicleImages extends ScreenComponent<
	AppState,
	PolicyCheckVehicleImagesState
> {
	constructor(props: AppState) {
		super(props);

		this.state = {};

		this.showPolicyCheckVehicleImagesExampleModal =
			this.showPolicyCheckVehicleImagesExampleModal.bind(this);
	}

	async showPolicyCheckVehicleImagesExampleModal() {
		const images: ExampleModalImage[] = [
			{
				src: `${process.env.PUBLIC_URL}/images/example-front.jpg`,
			},
			{
				src: `${process.env.PUBLIC_URL}/images/example-right.jpg`,
			},
			{
				src: `${process.env.PUBLIC_URL}/images/example-rear.jpg`,
			},
			{
				src: `${process.env.PUBLIC_URL}/images/example-left.jpg`,
			},
		];

		await this.props.showExampleModal(
			images,
			'vehicleImagesExampleModal.headline.first',
			'vehicleImagesExampleModal.button.close',
			'vehicle-side-images'
		);
	}

	public render() {
		return (
			<div className="main-container">
				<h2 className="policycheck-vehicle-images-title">
					<FormattedMessage id="vehicleImages.overviewPhotos" />
				</h2>
				<p className="policycheck-vehicle-images-paragraph">
					<FormattedMessage id="vehicleImages.description" />
				</p>

				<DetailsContainer>
					<div className="policycheck-vehicle-images-detail-container">
						<p className="policycheck-vehicle-images-details-first">
							<FormattedMessage id="vehicleImages.details.first" />{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'PolicyCheckVehicleImages.asyncAction' });
									});
								}}
							/>
						</p>
						<SelectImage
							componentId="vehicleFront"
							imageFileInputType={ImageFileInputType.CapturePicture}
							fileType={EasyClaimDocumentType.FrontPerspectivePicture}
							caseId={this.props.easyClaimCase?.caseId}
							easyClaimService={this.props.easyClaimService}
							currentImage={this.props.vehicleImageFront}
							title="vehicleImages.details.first"
							placeholder={<StyledPlaceholderFront />}
							marginBottom="1rem"
							onChange={(image) => {
								const isImageLoading = ImageSelectionService.isImageLoading(image);

								const newState = Object.assign({}, this.props, {
									vehicleImageFront: image,
									isImageLoading: isImageLoading,
								});

								void this.props.setAppState(newState);
							}}
							onShowExample={() => {
								void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
									reportError(error, {
										context: 'PolicyCheckVehicleImages.showPolicyCheckVehicleImagesExampleModal',
									});
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</div>
					<div className="policycheck-vehicle-images-detail-container">
						<p className="policycheck-vehicle-images-details-second">
							<FormattedMessage id="vehicleImages.details.second" />{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'PolicyCheckVehicleImages.asyncAction' });
									});
								}}
							/>
						</p>
						<SelectImage
							componentId="vehicleRight"
							imageFileInputType={ImageFileInputType.CapturePicture}
							fileType={EasyClaimDocumentType.RightPerspectivePicture}
							caseId={this.props.easyClaimCase?.caseId}
							easyClaimService={this.props.easyClaimService}
							currentImage={this.props.vehicleImageRight}
							title="vehicleImages.details.second"
							placeholder={<StyledPlaceholderRightSide />}
							marginBottom="1rem"
							onChange={(image) => {
								const isImageLoading = ImageSelectionService.isImageLoading(image);

								const newState = Object.assign({}, this.props, {
									vehicleImageRight: image,
									isImageLoading: isImageLoading,
								});

								void this.props.setAppState(newState);
							}}
							onShowExample={() => {
								void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
									reportError(error, {
										context: 'PolicyCheckVehicleImages.showPolicyCheckVehicleImagesExampleModal',
									});
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</div>
					<div className="policycheck-vehicle-images-detail-container">
						<p className="policycheck-vehicle-images-details-third">
							<FormattedMessage id="vehicleImages.details.third" />{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'PolicyCheckVehicleImages.asyncAction' });
									});
								}}
							/>
						</p>
						<SelectImage
							componentId="vehicleRear"
							imageFileInputType={ImageFileInputType.CapturePicture}
							fileType={EasyClaimDocumentType.RearPerspectivePicture}
							caseId={this.props.easyClaimCase?.caseId}
							easyClaimService={this.props.easyClaimService}
							currentImage={this.props.vehicleImageRear}
							title="vehicleImages.details.third"
							placeholder={<StyledPlaceholderRear />}
							marginBottom="1rem"
							onChange={(image) => {
								const isImageLoading = ImageSelectionService.isImageLoading(image);

								const newState = Object.assign({}, this.props, {
									vehicleImageRear: image,
									isImageLoading: isImageLoading,
								});

								void this.props.setAppState(newState);
							}}
							onShowExample={() => {
								void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
									reportError(error, {
										context: 'PolicyCheckVehicleImages.showPolicyCheckVehicleImagesExampleModal',
									});
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</div>
					<div className="policycheck-vehicle-images-detail-container">
						<p className="policycheck-vehicle-images-details-fourth">
							<FormattedMessage id="vehicleImages.details.fourth" />{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'PolicyCheckVehicleImages.asyncAction' });
									});
								}}
							/>
						</p>
						<SelectImage
							componentId="vehicleLeft"
							imageFileInputType={ImageFileInputType.CapturePicture}
							fileType={EasyClaimDocumentType.LeftPerspectivePicture}
							caseId={this.props.easyClaimCase?.caseId}
							easyClaimService={this.props.easyClaimService}
							currentImage={this.props.vehicleImageLeft}
							title="vehicleImages.details.fourth"
							placeholder={<StyledPlaceholderLeftSide />}
							marginBottom="1rem"
							onChange={(image) => {
								const isImageLoading = ImageSelectionService.isImageLoading(image);

								const newState = Object.assign({}, this.props, {
									vehicleImageLeft: image,
									isImageLoading: isImageLoading,
								});

								void this.props.setAppState(newState);
							}}
							onShowExample={() => {
								void this.showPolicyCheckVehicleImagesExampleModal().catch((error: unknown) => {
									reportError(error, {
										context: 'PolicyCheckVehicleImages.showPolicyCheckVehicleImagesExampleModal',
									});
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</div>
				</DetailsContainer>
			</div>
		);
	}
}

const StyledPlaceholderFront = styled(PlaceholderFrontV2)`
	fill: ${(p) => p.theme.colors.primary};
	color: ${(p) => p.theme.colors.primary};
	background-color: white;
	width: auto;
	display: block;
	border-radius: 3px;
	opacity: 0.95;
`;

const StyledPlaceholderRear = styled(PlaceholderRearV2)`
	fill: ${(p) => p.theme.colors.primary};
	color: ${(p) => p.theme.colors.primary};
	background-color: white;
	width: auto;
	display: block;
	border-radius: 3px;
	opacity: 0.95;
`;

const StyledPlaceholderRightSide = styled(PlaceholderRightSideV2)`
	fill: ${(p) => p.theme.colors.primary};
	color: ${(p) => p.theme.colors.primary};
	background-color: white;
	width: auto;
	display: block;
	border-radius: 3px;
	opacity: 0.95;
`;

const StyledPlaceholderLeftSide = styled(PlaceholderLeftSideV2)`
	fill: ${(p) => p.theme.colors.primary};
	color: ${(p) => p.theme.colors.primary};
	background-color: white;
	width: auto;
	display: block;
	border-radius: 3px;
	opacity: 0.95;
`;
